import { Trans, useLingui } from "@lingui/react/macro";
import { ApiError, apiFetch } from "@pontimed/api-client";
import { createFileRoute } from "@tanstack/react-router";
import { CheckCircle2, Loader2, MailCheck, MailX, type LucideIcon } from "lucide-react";
import { useEffect, useState, type CSSProperties } from "react";

import { Button } from "@/components/ui/button";

interface VerifySearch {
  /** Single-use magic-link token issued by /api/auth/send-email-otp. */
  token?: string;
  /** Address the link was issued for — POST /verify-email-magic requires it. */
  email?: string;
  /** Optional return path after successful sign-in. */
  next?: string;
  /**
   * "1" when the user had "keep me signed in 30 days" checked in the modal
   * that requested this link. Carried in the URL because this page is usually
   * opened from the mail client — a different tab (often a different browser)
   * where the modal's `pontimed_remember_me` localStorage value is unreachable.
   */
  remember?: string;
  /**
   * Legacy auth_verify=1 marker — Auth.js redirected here after the magic
   * link was emailed (NOT after click). MVP shows a friendly "Check your
   * inbox" state when token is missing but auth_verify=1 is present.
   */
  auth_verify?: string;
}

const DEFAULT_RETURN = "/account/user/dashboard";

/** Same-origin relative paths only — resolved the way location.assign will. */
/**
 * Other hosts a magic link may return to: the sign-in started on www or the
 * admin. The API checked `next` against the same list (ALLOWED_NEXT_HOSTS in
 * apps/api/src/routes/auth-oauth.ts) before mailing it; checked again here
 * because the link is editable.
 */
const OTHER_RETURN_HOSTS = new Set(["pontimed.app", "www.pontimed.app", "admin.pontimed.app"]);

function sanitizeNext(value: string | undefined): string | undefined {
  if (!value || typeof window === "undefined") return undefined;
  try {
    const url = new URL(value, window.location.origin);
    if (url.origin === window.location.origin) return url.pathname + url.search + url.hash;
    const here = window.location.hostname;
    const isDev = here === "localhost" || here.endsWith(".pontimed.local");
    const allowed =
      (url.protocol === "https:" && OTHER_RETURN_HOSTS.has(url.hostname)) ||
      (isDev && (url.hostname === "localhost" || url.hostname.endsWith(".pontimed.local")));
    return allowed ? url.href : undefined;
  } catch {
    return undefined;
  }
}

export const Route = createFileRoute("/auth/verify")({
  validateSearch: (search: Record<string, unknown>): VerifySearch => ({
    token: typeof search.token === "string" ? search.token : undefined,
    email: typeof search.email === "string" ? search.email : undefined,
    next: typeof search.next === "string" ? search.next : undefined,
    auth_verify: typeof search.auth_verify === "string" ? search.auth_verify : undefined,
    remember: typeof search.remember === "string" ? search.remember : undefined,
  }),
  component: VerifyPage,
});

type Status = "idle" | "verifying" | "success" | "error" | "inbox";

function VerifyPage() {
  const { token, email, next, auth_verify, remember } = Route.useSearch();
  const { t } = useLingui();
  const [status, setStatus] = useState<Status>(() => {
    if (token) return "idle";
    if (auth_verify === "1") return "inbox";
    return "error";
  });
  const [error, setError] = useState<string | null>(
    token || auth_verify === "1" ? null : t`Missing magic link token`,
  );

  // Auto-verify on mount when a token is present in the URL.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setStatus("verifying");
    setError(null);
    apiFetch
      .post(
        "/api/auth/verify-email-magic",
        { token, email, rememberMe: remember === "1" },
        { skipAuthRefresh: true },
      )
      .then(() => {
        if (cancelled) return;
        setStatus("success");
        // Hard navigation so apps/app session hook re-reads the new
        // pontimed_at + pontimed_rt cookies issued by /api/auth/verify-email-magic.
        // `next` is attacker-controllable (anyone can craft the URL); only a
        // same-origin path may reach window.location.assign — same rule as
        // /auth/2fa-challenge.
        const target = sanitizeNext(next) ?? DEFAULT_RETURN;
        if (typeof window !== "undefined") {
          window.setTimeout(() => window.location.assign(target), 600);
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError) {
          // POST /verify-email-magic answers 401 for an expired/unknown token
          // and 400 when the email is missing from the link.
          if (err.status === 410 || err.status === 401)
            setError(t`This link has expired. Request a new one to continue.`);
          else if (err.status === 404 || err.status === 400)
            setError(t`Link not recognised. Request a new one to continue.`);
          else setError(t`Verification failed`);
        } else {
          setError(err instanceof Error ? err.message : t`Verification failed`);
        }
        setStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, [token, email, next, remember, t]);

  return (
    <main className="relative isolate flex min-h-dvh items-center justify-center px-4 py-10">
      <StatusBackdrop />
      <div className={STATUS_CARD} style={STATUS_CARD_STYLE}>
        {status === "verifying" && <VerifyingState />}
        {status === "success" && <SuccessState />}
        {status === "inbox" && <InboxState />}
        {(status === "error" || status === "idle") && status !== "idle" && (
          <ErrorState message={error ?? t`Couldn't verify magic link`} />
        )}
        {status === "idle" && <VerifyingState />}
      </div>
    </main>
  );
}

function VerifyingState() {
  return (
    <div className="flex flex-col items-center gap-4 text-center">
      <StatusTile icon={Loader2} tone="primary" spin />
      <h1 className={STATUS_TITLE} style={STAGGER_1}>
        <Trans>Signing you in…</Trans>
      </h1>
      <p className={STATUS_BODY} style={STAGGER_2}>
        <Trans>Verifying your one-tap link.</Trans>
      </p>
    </div>
  );
}

function SuccessState() {
  return (
    <div className="flex flex-col items-center gap-4 text-center">
      <StatusTile icon={CheckCircle2} tone="success" />
      <h1 className={STATUS_TITLE} style={STAGGER_1}>
        <Trans>You're signed in</Trans>
      </h1>
      <p className={STATUS_BODY} style={STAGGER_2}>
        <Trans>Redirecting to your dashboard…</Trans>
      </p>
    </div>
  );
}

function InboxState() {
  return (
    <div className="flex flex-col items-center gap-4 text-center">
      <StatusTile icon={MailCheck} tone="primary" />
      <h1 className={STATUS_TITLE} style={STAGGER_1}>
        <Trans>Check your inbox</Trans>
      </h1>
      <p className={STATUS_BODY} style={STAGGER_2}>
        <Trans>
          We sent a one-tap sign-in link to your email. Open it on this device to continue.
        </Trans>
      </p>
      <Button
        variant="outline"
        className={`mt-2 ${BTN_SECONDARY}`}
        onClick={() => {
          if (typeof window === "undefined") return;
          window.location.assign("/find?auth=open");
        }}
      >
        <Trans>Back to login</Trans>
      </Button>
    </div>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center gap-4 text-center">
      <StatusTile icon={MailX} tone="destructive" />
      <h1 className={STATUS_TITLE} style={STAGGER_1}>
        <Trans>Couldn't sign you in</Trans>
      </h1>
      <p className={STATUS_BODY} style={STAGGER_2}>
        {message}
      </p>
      <Button
        className={`mt-2 ${BTN_PRIMARY}`}
        onClick={() => {
          if (typeof window === "undefined") return;
          window.location.assign("/find?auth=open");
        }}
      >
        <Trans>Try again</Trans>
      </Button>
    </div>
  );
}

/* ─── Puls status screen ───
   Same language as the apps/web Puls kit (`components/puls/primitives.tsx`,
   not importable from apps/app): blueprint grid + soft glow behind one
   centred card, state icon in a toned tile, display heading, 48px buttons.
   Presentational only. */

const GRID_LINE = "color-mix(in oklch, var(--primary) 8%, transparent)";
const GRID_MASK = "radial-gradient(ellipse 70% 60% at 50% 40%, black 15%, transparent 72%)";
const GRID_STYLE: CSSProperties = {
  backgroundImage: `linear-gradient(${GRID_LINE} 1px, transparent 1px), linear-gradient(90deg, ${GRID_LINE} 1px, transparent 1px)`,
  backgroundSize: "44px 44px",
  maskImage: GRID_MASK,
  WebkitMaskImage: GRID_MASK,
};
const GLOW_STYLE: CSSProperties = {
  backgroundImage:
    "radial-gradient(closest-side, color-mix(in oklch, var(--primary) 16%, transparent), color-mix(in oklch, var(--accent) 7%, transparent) 55%, transparent)",
};
/** Soft primary wash at the top of the card. */
const STATUS_CARD_STYLE: CSSProperties = {
  backgroundImage:
    "linear-gradient(180deg, color-mix(in oklch, var(--primary-soft) 55%, var(--card)) 0%, var(--card) 45%)",
};
const STATUS_CARD =
  "border-border bg-card w-full max-w-md rounded-2xl border p-6 shadow-[0_30px_70px_-45px_color-mix(in_oklch,var(--primary)_60%,transparent)] sm:p-8";
const STATUS_TITLE =
  "puls-reveal font-display text-foreground m-0 text-2xl leading-tight font-semibold tracking-[-0.025em] text-balance";
const STATUS_BODY = "puls-reveal text-muted-foreground m-0 text-sm leading-relaxed";
/** `.puls-reveal` stagger steps (80 ms each, see puls.css). */
const STAGGER_1 = { "--puls-i": 1 } as CSSProperties;
const STAGGER_2 = { "--puls-i": 2 } as CSSProperties;
const BTN = "h-12 w-full gap-2 rounded-lg px-6 text-sm font-semibold";
const BTN_PRIMARY = `${BTN} hover:bg-primary-hover`;
const BTN_SECONDARY = `${BTN} bg-card text-foreground hover:border-primary/40 hover:bg-card hover:text-primary`;

type StatusTone = "primary" | "success" | "destructive";

const TILE_TONE: Record<StatusTone, string> = {
  primary: "border-primary/20 bg-primary-soft text-primary",
  success: "border-success-border bg-success-soft text-success",
  destructive: "border-destructive-border bg-destructive-soft text-destructive",
};

function StatusBackdrop() {
  return (
    <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
      <div className="absolute inset-0" style={GRID_STYLE} />
      <div
        className="absolute top-[10%] left-1/2 h-[70%] w-[min(92%,44rem)] -translate-x-1/2 blur-[20px]"
        style={GLOW_STYLE}
      />
    </div>
  );
}

function StatusTile({
  icon: Icon,
  tone,
  spin = false,
}: {
  icon: LucideIcon;
  tone: StatusTone;
  spin?: boolean;
}) {
  return (
    <span
      aria-hidden="true"
      className={`puls-reveal grid size-14 shrink-0 place-items-center rounded-2xl border ${TILE_TONE[tone]}`}
    >
      <Icon className={spin ? "size-6 animate-spin" : "size-6"} />
    </span>
  );
}
